import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessagesSquare,
  Plus,
  RefreshCw,
  Search,
  Filter,
  Users,
  MessageCircle,
  Clock,
  ShieldCheck,
  ChevronRight,
  MoreVertical,
  Edit2,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Calendar,
  Sparkles,
  ArrowUpRight
} from "lucide-react";
import {
  useGetChatGroupsQuery,
  useCreateChatGroupMutation,
  useUpdateChatGroupMutation,
  useDeleteChatGroupMutation,
  useSyncAllChatGroupsMutation,
  ChatGroup
} from "@/redux/slices/chatApi";
import { useGetAcademicYearsQuery } from "@/redux/slices/academicYearApi";
import { useGetStudentsQuery, Student } from "@/redux/slices/studentApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { format } from "date-fns";

export default function ChatGroups() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [page, setPage] = useState(1);

  // Queries & Mutations
  const { data, isLoading, isFetching, refetch } = useGetChatGroupsQuery({
    search: searchTerm,
    status: statusFilter,
    yearId: yearFilter,
    page,
    limit: 20
  });

  const { data: yearsData } = useGetAcademicYearsQuery({ limit: 100 });
  const { data: studentsResponse, isLoading: isStudentsLoading } = useGetStudentsQuery({ limit: 1000 });
  const allStudents: Student[] = Array.isArray(studentsResponse?.data) ? studentsResponse.data : [];

  const [createGroup, { isLoading: isCreating }] = useCreateChatGroupMutation();
  const [updateGroup, { isLoading: isUpdating }] = useUpdateChatGroupMutation();
  const [deleteGroup, { isLoading: isDeleting }] = useDeleteChatGroupMutation();
  const [syncAll, { isLoading: isSyncing }] = useSyncAllChatGroupsMutation();

  // Modals state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ChatGroup | null>(null);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);

  // Student selection in Create Group modal
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [studentYearFilter, setStudentYearFilter] = useState<string>("all");
  const [studentSearch, setStudentSearch] = useState<string>("");

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    yearId: "",
    educationYear: "",
    description: "",
    status: "active" as "active" | "inactive"
  });

  const handleOpenCreate = () => {
    setFormData({
      name: "",
      yearId: "",
      educationYear: "",
      description: "",
      status: "active"
    });
    setSelectedStudentIds([]);
    setStudentYearFilter("all");
    setStudentSearch("");
    setIsCreateModalOpen(true);
  };

  const handleOpenEdit = (group: ChatGroup) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      yearId: group.yearId || "",
      educationYear: group.educationYear,
      description: group.description || "",
      status: group.status
    });
  };

  const handleYearSelectChange = (yearId: string) => {
    if (yearId === "custom") {
      setFormData(prev => ({ ...prev, yearId: "", educationYear: "", name: "" }));
      setStudentYearFilter("all");
      return;
    }
    const selectedYear = yearsData?.data?.find((y: any) => y.id === yearId);
    if (selectedYear) {
      setFormData(prev => ({
        ...prev,
        yearId: selectedYear.id,
        educationYear: selectedYear.name,
        name: `${selectedYear.name} Chat Group`
      }));

      const matchingStudents = allStudents.filter(s => s.yearId === selectedYear.id);
      if (matchingStudents.length > 0) {
        setStudentYearFilter(selectedYear.id);
        setSelectedStudentIds(matchingStudents.map(s => s.id));
      } else {
        // Default to all students so admin can select any enrolled student
        setStudentYearFilter("all");
      }
    }
  };

  // Filtered students for the modal list
  const filteredStudents = allStudents.filter(s => {
    let matchesYear = true;
    if (studentYearFilter === "unassigned") {
      matchesYear = !s.yearId;
    } else if (studentYearFilter !== "all") {
      matchesYear = s.yearId === studentYearFilter;
    }

    const searchLower = studentSearch.toLowerCase().trim();
    const matchesSearch = !searchLower ||
      s.name?.toLowerCase().includes(searchLower) ||
      s.mobile?.includes(searchLower) ||
      s.email?.toLowerCase().includes(searchLower);
    return matchesYear && matchesSearch;
  });

  const toggleStudent = (id: string) => {
    setSelectedStudentIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredStudents.map(s => s.id);
    setSelectedStudentIds(prev => Array.from(new Set([...prev, ...filteredIds])));
  };

  const handleDeselectAllFiltered = () => {
    const filteredSet = new Set(filteredStudents.map(s => s.id));
    setSelectedStudentIds(prev => prev.filter(id => !filteredSet.has(id)));
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.educationYear.trim()) {
      toast.error("Please fill in Group Name and Education Year.");
      return;
    }

    try {
      await createGroup({
        ...formData,
        studentIds: selectedStudentIds.length > 0 ? selectedStudentIds : undefined
      } as any).unwrap();
      toast.success("Chat group created successfully!");
      setIsCreateModalOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to create group");
    }
  };

  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGroup) return;

    try {
      await updateGroup({ id: editingGroup.id, ...formData }).unwrap();
      toast.success("Chat group updated successfully!");
      setEditingGroup(null);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update group");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingGroupId) return;
    try {
      await deleteGroup(deletingGroupId).unwrap();
      toast.success("Chat group deleted successfully!");
      setDeletingGroupId(null);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete group");
    }
  };

  const handleSyncAll = async () => {
    try {
      const res = await syncAll().unwrap();
      toast.success(res.message || "Chat groups synchronized successfully!");
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to sync groups");
    }
  };

  const groups = Array.isArray(data?.data) ? data.data : [];
  const pagination = data?.pagination;

  // Aggregate stats
  const totalGroups = groups.length;
  const totalActive = groups.filter(g => g.status === "active").length;
  const totalMembersEnrolled = groups.reduce((acc, g) => acc + (g.stats?.totalMembers || 0), 0);
  const totalMessagesCount = groups.reduce((acc, g) => acc + (g.stats?.totalMessages || 0), 0);

  return (
    <div className="min-h-screen bg-background/50 pb-20">
      {/* Header */}
      <div className="border-b bg-card/60 backdrop-blur-md sticky top-0 z-20 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
              <MessagesSquare className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-foreground">Year-Wise Chatting Groups</h1>
              <p className="text-xs text-muted-foreground">Manage academic year cohorts, student discussions, files and moderation.</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncAll}
              disabled={isSyncing}
              className="rounded-xl h-9 text-xs font-semibold"
            >
              {isSyncing ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Sync All Years
                </>
              )}
            </Button>
            <Button
              size="sm"
              onClick={handleOpenCreate}
              className="rounded-xl h-9 px-4 text-xs font-bold shadow-md bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Plus className="mr-1.5 h-4 w-4" /> Create Group
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-6">
        {/* Metric Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border shadow-xs bg-card/70 backdrop-blur-md rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Total Groups</p>
                <h3 className="text-2xl font-black tracking-tight text-foreground mt-1">{totalGroups}</h3>
              </div>
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <MessagesSquare className="h-5 w-5" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2 font-medium">{totalActive} Active Cohorts</p>
          </Card>

          <Card className="border shadow-xs bg-card/70 backdrop-blur-md rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Enrolled Students</p>
                <h3 className="text-2xl font-black tracking-tight text-foreground mt-1">{totalMembersEnrolled}</h3>
              </div>
              <div className="h-10 w-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                <Users className="h-5 w-5" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2 font-medium">Synced with Academic Years</p>
          </Card>

          <Card className="border shadow-xs bg-card/70 backdrop-blur-md rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Total Messages</p>
                <h3 className="text-2xl font-black tracking-tight text-foreground mt-1">{totalMessagesCount}</h3>
              </div>
              <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                <MessageCircle className="h-5 w-5" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2 font-medium">Text, PDFs & Attachments</p>
          </Card>

          <Card className="border shadow-xs bg-card/70 backdrop-blur-md rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Security & Policy</p>
                <h3 className="text-2xl font-black tracking-tight text-foreground mt-1">Admin Monitored</h3>
              </div>
              <div className="h-10 w-10 rounded-xl bg-orange-500/10 flex items-center justify-center text-orange-500">
                <ShieldCheck className="h-5 w-5" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2 font-medium">Soft-delete & Member locks</p>
          </Card>
        </div>

        {/* Filters Bar */}
        <Card className="border shadow-xs bg-card/70 backdrop-blur-md rounded-2xl p-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search groups by name or year..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                className="pl-9 h-10 rounded-xl text-xs"
              />
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto">
              <Select value={yearFilter} onValueChange={(v) => { setYearFilter(v); setPage(1); }}>
                <SelectTrigger className="h-10 rounded-xl text-xs w-full md:w-44">
                  <SelectValue placeholder="All Academic Years" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">All Academic Years</SelectItem>
                  {yearsData?.data?.map((y: any) => (
                    <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="h-10 rounded-xl text-xs w-full md:w-36">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="inactive">Inactive Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Groups Grid / List */}
        {isLoading ? (
          <div className="h-64 flex flex-col items-center justify-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-xs font-medium text-muted-foreground">Loading chat groups...</p>
          </div>
        ) : groups.length === 0 ? (
          <Card className="border-dashed p-12 text-center rounded-2xl">
            <div className="h-14 w-14 rounded-3xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-4">
              <MessagesSquare className="h-7 w-7" />
            </div>
            <h3 className="text-base font-bold text-foreground">No Chat Groups Found</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
              No chat groups have been created yet or none match your filter. Click "Sync All Years" or "Create Group" to get started.
            </p>
            <div className="flex items-center justify-center gap-3 mt-6">
              <Button onClick={handleSyncAll} disabled={isSyncing} variant="outline" size="sm" className="rounded-xl font-bold">
                <RefreshCw className="mr-2 h-4 w-4" /> Sync All Years
              </Button>
              <Button onClick={handleOpenCreate} size="sm" className="rounded-xl font-bold">
                <Plus className="mr-2 h-4 w-4" /> Create First Group
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groups.map((group) => {
              const isActive = group.status === "active";
              const stats = group.stats;
              const lastMsg = group.lastMessage;

              return (
                <Card
                  key={group.id}
                  className="border shadow-xs bg-card/80 hover:bg-card hover:shadow-md transition-all duration-300 rounded-2xl overflow-hidden flex flex-col justify-between group"
                >
                  <div>
                    {/* Card Header */}
                    <div className="p-5 pb-4 border-b border-border/40">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center text-primary font-bold shadow-xs">
                            <MessagesSquare className="h-5 w-5" />
                          </div>
                          <div>
                            <h3 className="text-sm font-bold text-foreground leading-snug group-hover:text-primary transition-colors">
                              {group.name}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-[10px] font-mono font-bold bg-muted/50">
                                {group.educationYear}
                              </Badge>
                              <Badge className={cn("text-[9px] uppercase font-bold tracking-wider", isActive ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30" : "bg-muted text-muted-foreground")}>
                                {isActive ? "Active" : "Inactive"}
                              </Badge>
                            </div>
                          </div>
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl text-muted-foreground hover:text-foreground">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="rounded-xl w-40">
                            <DropdownMenuItem onClick={() => navigate(`/chat-groups/${group.id}`)} className="cursor-pointer text-xs font-semibold">
                              <ArrowUpRight className="mr-2 h-3.5 w-3.5 text-primary" /> Open Chat
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenEdit(group)} className="cursor-pointer text-xs font-semibold">
                              <Edit2 className="mr-2 h-3.5 w-3.5" /> Edit Group
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDeletingGroupId(group.id)}
                              className="cursor-pointer text-xs font-semibold text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete Group
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {group.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-3 font-normal">
                          {group.description}
                        </p>
                      )}
                    </div>

                    {/* Stats & Last Message */}
                    <div className="p-5 space-y-4">
                      <div className="grid grid-cols-3 gap-2 p-3 rounded-xl bg-muted/40 border border-border/40 text-center">
                        <div>
                          <p className="text-[10px] font-bold text-muted-foreground uppercase">Members</p>
                          <p className="text-sm font-black text-foreground mt-0.5">{stats?.totalMembers || 0}</p>
                        </div>
                        <div className="border-x border-border/40">
                          <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase">Active</p>
                          <p className="text-sm font-black text-emerald-600 dark:text-emerald-400 mt-0.5">{stats?.activeMembers || 0}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-muted-foreground uppercase">Messages</p>
                          <p className="text-sm font-black text-foreground mt-0.5">{stats?.totalMessages || 0}</p>
                        </div>
                      </div>

                      {/* Last Message Snippet */}
                      <div className="text-xs bg-card/60 p-2.5 rounded-xl border border-border/30">
                        {lastMsg ? (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-[11px] text-muted-foreground font-semibold">
                              <span className="truncate max-w-[140px] text-foreground font-bold">{lastMsg.senderName}</span>
                              <span>{format(new Date(lastMsg.createdAt), "MMM d, h:mm a")}</span>
                            </div>
                            <p className="text-[11px] text-muted-foreground line-clamp-1 italic">
                              {lastMsg.messageText || (lastMsg.messageType !== "TEXT" ? `[${lastMsg.messageType} Attachment]` : "No content")}
                            </p>
                          </div>
                        ) : (
                          <p className="text-[11px] text-muted-foreground italic">No messages sent yet in this group.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Card Action Footer */}
                  <div className="p-4 pt-0">
                    <Button
                      onClick={() => navigate(`/chat-groups/${group.id}`)}
                      className="w-full h-10 rounded-xl font-bold text-xs bg-primary/10 hover:bg-primary hover:text-primary-foreground text-primary transition-all duration-200 gap-1.5 shadow-none group-hover:bg-primary group-hover:text-primary-foreground"
                    >
                      <span>Enter Group Chat & Manage</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* CREATE GROUP MODAL */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <MessagesSquare className="h-5 w-5 text-primary" /> Create New Chat Group
            </DialogTitle>
            <DialogDescription className="text-xs">
              Establish a new cohort chatting group, link an academic year, and choose students to add.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateSubmit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-bold">Link to Academic Year</Label>
              <Select value={formData.yearId || "custom"} onValueChange={handleYearSelectChange}>
                <SelectTrigger className="h-11 rounded-xl">
                  <SelectValue placeholder="Select Academic Year..." />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="custom">Custom Cohort / Year</SelectItem>
                  {yearsData?.data?.map((y: any) => (
                    <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold">Group Name</Label>
                <Input
                  placeholder="e.g. 2024-25 Group"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="h-11 rounded-xl text-xs"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold">Education Year</Label>
                <Input
                  placeholder="e.g. 2024-25"
                  value={formData.educationYear}
                  onChange={(e) => setFormData(prev => ({ ...prev, educationYear: e.target.value }))}
                  className="h-11 rounded-xl text-xs"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold">Description (Optional)</Label>
              <Textarea
                placeholder="Group purpose, rules and details for students..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="min-h-[70px] rounded-xl text-xs"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold">Initial Status</Label>
              <Select value={formData.status} onValueChange={(v: any) => setFormData(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="h-11 rounded-xl text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="active">Active (Accessible to enrolled students)</SelectItem>
                  <SelectItem value="inactive">Inactive (Archived)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* STUDENT SELECTION SECTION WITH YEAR-WISE FILTER */}
            <div className="border rounded-2xl p-4 bg-muted/20 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <Label className="text-xs font-bold text-foreground">Select Students to Add</Label>
                  <Badge variant="secondary" className="text-[10px] px-2 py-0.5 rounded-lg font-semibold">
                    {selectedStudentIds.length} Selected
                  </Badge>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllFiltered}
                    className="h-7 text-[11px] rounded-lg px-2 font-medium"
                    disabled={filteredStudents.length === 0}
                  >
                    Select All ({filteredStudents.length})
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleDeselectAllFiltered}
                    className="h-7 text-[11px] rounded-lg px-2 text-muted-foreground hover:text-foreground"
                    disabled={selectedStudentIds.length === 0}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              {/* Filter controls */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Select value={studentYearFilter} onValueChange={setStudentYearFilter}>
                    <SelectTrigger className="h-9 rounded-xl text-xs bg-background">
                      <SelectValue placeholder="Filter by Student Academic Year" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="all">All Students ({allStudents.length})</SelectItem>
                      {yearsData?.data?.map((y: any) => {
                        const count = allStudents.filter(s => s.yearId === y.id).length;
                        return (
                          <SelectItem key={y.id} value={y.id}>
                            {y.name} ({count})
                          </SelectItem>
                        );
                      })}
                      {allStudents.some(s => !s.yearId) && (
                        <SelectItem value="unassigned">
                          Unassigned ({allStudents.filter(s => !s.yearId).length})
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, mobile, email..."
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    className="h-9 pl-8 rounded-xl text-xs bg-background"
                  />
                </div>
              </div>

              {/* Student checklist */}
              <div className="max-h-48 overflow-y-auto border rounded-xl bg-background divide-y">
                {isStudentsLoading ? (
                  <div className="p-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" /> Loading students...
                  </div>
                ) : filteredStudents.length === 0 ? (
                  <div className="p-6 text-center text-xs text-muted-foreground">
                    No students match the selected academic year or search filter.
                  </div>
                ) : (
                  filteredStudents.map((st) => {
                    const isChecked = selectedStudentIds.includes(st.id);
                    const yearName = yearsData?.data?.find((y: any) => y.id === st.yearId)?.name;

                    return (
                      <div
                        key={st.id}
                        onClick={() => toggleStudent(st.id)}
                        className={cn(
                          "flex items-center gap-3 p-2.5 hover:bg-muted/40 cursor-pointer transition-colors text-xs",
                          isChecked && "bg-primary/5"
                        )}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleStudent(st.id)}
                          className="rounded-md"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground truncate">{st.name}</span>
                            {yearName && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">
                                {yearName}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {st.mobile || st.email || "No contact info"}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <DialogFooter className="pt-3 gap-2">
              <Button type="button" variant="ghost" onClick={() => setIsCreateModalOpen(false)} className="rounded-xl h-10 text-xs font-semibold">
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating} className="rounded-xl h-10 px-5 text-xs font-bold shadow-sm">
                {isCreating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                Create Group ({selectedStudentIds.length} Members)
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* EDIT GROUP MODAL */}
      <Dialog open={!!editingGroup} onOpenChange={(open) => !open && setEditingGroup(null)}>
        <DialogContent className="sm:max-w-[480px] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-primary" /> Edit Chat Group
            </DialogTitle>
            <DialogDescription className="text-xs">
              Update group name, description, and status.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpdateSubmit} className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold">Group Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="h-11 rounded-xl"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold">Education Year</Label>
                <Input
                  value={formData.educationYear}
                  onChange={(e) => setFormData(prev => ({ ...prev, educationYear: e.target.value }))}
                  className="h-11 rounded-xl"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="min-h-[80px] rounded-xl text-xs"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold">Status</Label>
              <Select value={formData.status} onValueChange={(v: any) => setFormData(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="pt-3 gap-2">
              <Button type="button" variant="ghost" onClick={() => setEditingGroup(null)} className="rounded-xl h-10 text-xs font-semibold">
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdating} className="rounded-xl h-10 px-5 text-xs font-bold shadow-sm">
                {isUpdating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog open={!!deletingGroupId} onOpenChange={(open) => !open && setDeletingGroupId(null)}>
        <DialogContent className="sm:max-w-[420px] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-base text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" /> Delete Chat Group?
            </DialogTitle>
            <DialogDescription className="text-xs">
              This action will permanently remove this chat group and all associated message records.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3 gap-2">
            <Button variant="ghost" onClick={() => setDeletingGroupId(null)} className="rounded-xl h-10 text-xs font-semibold">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={isDeleting} className="rounded-xl h-10 text-xs font-bold shadow-sm">
              {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Confirm Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
